import React, { KeyboardEvent, useEffect, useState } from 'react'
import { useFormValidation, useInputLabel, useInputValue } from '@/form-control'

import { cn } from '@/utils/cn'

type TagFieldProps<FormValuesType extends { [key: string]: any } = any> = {
    // Inside FormWrapper props
    field?: string
    formValues?: FormValuesType
    setFormValues?: (values: FormValuesType) => void
    formOptions?: any
    // Standalone props
    value?: string[] | undefined
    onChange?: (value: string[]) => void
    // Styling props
    label?: string
    containerClass?: string
    labelClass?: string
    inputClass?: string
    tagContainerClass?: string
    tagClass?: string
    inputFieldName?: string
    placeholder?: string
} & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'>

export default function TagField<FormValuesType extends { [key: string]: any } = any>({
    // Inside FormWrapper
    field,
    formValues,
    setFormValues,
    formOptions,
    // Standalone
    value,
    onChange,
    //
    label,
    containerClass,
    labelClass,
    inputClass,
    tagContainerClass,
    tagClass,
    inputFieldName,
    placeholder = 'Type and press Enter to add tags',
    ...rest
}: TagFieldProps<FormValuesType>) {
    // Handle the current input text
    const [inputText, setInputText] = useState('')

    // Handle the tags array
    const { inputValue: tags, handleChange: handleTagsChange } = useInputValue(
        value,
        onChange,
        field,
        formValues,
        setFormValues,
        formOptions
    )

    const { inputLabel } = useInputLabel(label, field, formOptions)
    const { isValid, ValidationPrompt } = useFormValidation(formValues ?? {}, tags, field, formOptions)

    // Ensure tags is always an array
    useEffect(() => {
        if (!Array.isArray(tags)) {
            handleTagsChange([])
        }
    }, [tags])

    function addTagsFromInput(input: string) {
        if (!input.trim()) return

        const currentTags = input
            .split(',')
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0)

        const existingTags = Array.isArray(tags) ? tags : []
        if (currentTags.length > 0) {
            const newTags = [...existingTags, ...currentTags]
            handleTagsChange(newTags)
        }

        setInputText('')
    }

    // Handle Enter key press to add a tag
    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault()
            addTagsFromInput(inputText.trim())
        }
    }

    // Handle removing a tag when clicked
    const removeTag = (index: number) => {
        const newTags = Array.isArray(tags) ? [...tags.slice(0, index), ...tags.slice(index + 1)] : []
        handleTagsChange(newTags)
    }
    function clearAllTags() {
        handleTagsChange([])
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value

        const lastChar = newValue.slice(-1)
        if ([',', '.', '-'].includes(lastChar)) {
            addTagsFromInput(newValue.slice(0, -1))
        } else {
            setInputText(newValue)
        }
    }

    const currentTags = Array.isArray(tags) ? tags : []
    return (
        <div className={cn('field-container', containerClass)}>
            {inputLabel && (
                <div className='flex items-center justify-between'>
                    <label className={cn('field-label', labelClass)}>
                        {inputLabel} {rest?.required && <span className='text-red-500'>*</span>}
                    </label>
                    {currentTags.length > 0 && (
                        <button
                            type='button'
                            onClick={clearAllTags}
                            className='text-xs text-gray-400 hover:text-gray-600 transition-colors'
                            title='Clear all tags'
                        >
                            Clear all
                        </button>
                    )}
                </div>
            )}

            <textarea
                {...rest}
                className={cn(
                    'field-input border-gray-200 focus:ring-gray-200 pt-1',
                    inputClass,
                    isValid ? '' : 'ring-amber-600 focus-within:ring-amber-600'
                )}
                value={inputText as string}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                    if (inputText.trim()) {
                        addTagsFromInput(inputText)
                    }
                }}
                name={inputFieldName ? inputFieldName : field}
                placeholder={placeholder}
            />

            {Array.isArray(tags) && currentTags.length > 0 && (
                <div className={cn('flex flex-wrap gap-2 mt-2', tagContainerClass)}>
                    {tags.map((tag, index) => (
                        <div
                            key={index}
                            className={cn(
                                'px-3 py-1 mt-3 shadow-sm bg-secondary-100/60  max-w-[200px] truncate flex rounded-full cursor-pointe text-amber-600 hover:bg-secondary-100/60 transition-colors',
                                tagClass
                            )}
                            onClick={() => removeTag(index)}
                            title='Click to remove'
                        >
                            {tag}
                            <button
                                type='button'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    removeTag(index)
                                }}
                                className='ml-1 text-amber-600 hover:text-amber-600  rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold transition-colors'
                                aria-label={`Remove ${tag} tag`}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <ValidationPrompt />
        </div>
    )
}
